import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchPublicTopic } from "../../publicShareApi";
import { usePublicTTS } from "../../hooks/usePublicTTS";
import { track } from "../../utils/analytics";
import GuidedTopicJourney from "../GuidedTopicJourney";
import ListeningPanel from "../ListeningPanel";
import YouTubeSyncPlayer from "../YouTubeSyncPlayer";
import PublicScenario from "./PublicScenario";
import PublicWordBubble from "./PublicWordBubble";
import WordMorphology from "../WordMorphology";
import PublicWordDetail from "./PublicWordDetail";
import PublicVocabList from "./PublicVocabList";
import PublicLockedStep from "./PublicLockedStep";
import PublicShareButton from "./PublicShareButton";

const LISTENING_PLAYS_REQUIRED = 3;

// Synthesized read-only progression (shape of learning.topic_progression's
// progression_payload). Steps 1–3 are open; Role Play and Quiz need an account.
// YouTube topics have no Role Play step (mirrors topic_progression.steps_for).
function publicSteps(viewStep, playCount, hasRoleplay) {
  const defs = [
    { key: "reading", title: "Reading", locked: false },
    { key: "vocabulary", title: "Vocabulary", locked: false },
    { key: "listening", title: "Listening", locked: false },
    ...(hasRoleplay ? [{ key: "roleplay", title: "Role Play", locked: true }] : []),
    { key: "quiz", title: "Quiz", locked: true },
  ];
  return {
    steps: defs.map((d, i) => ({
      ...d,
      order: i + 1,
      completed: false,
      current: d.key === viewStep,
    })),
    current_step: viewStep,
    listening_play_count: playCount,
    topic_completed: false,
  };
}

/**
 * PublicTopicPage — the anonymous shared-topic page at /t/<token>.
 *
 * Mounted from index.js BEFORE App (like /irab and /start), so no auth gate
 * or app chrome ever runs here: no sidebar, no account toolbar. Shows the
 * topic header, the Guided Journey with steps 1–3 usable (Reading /
 * Vocabulary / Listening) and 4–5 behind a signup banner, tappable words
 * with a read-only word-detail panel, and cached TTS. No AI features.
 */
export default function PublicTopicPage() {
  const { token } = useParams();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gone, setGone] = useState(false);
  // The share link can name the step the admin was viewing when they shared
  // (?step=vocabulary|listening); anything else opens on Reading.
  const [viewStep, setViewStep] = useState(() => {
    const step = new URLSearchParams(window.location.search).get("step");
    return ["reading", "vocabulary", "listening"].includes(step) ? step : "reading";
  });
  const [playCount, setPlayCount] = useState(0); // local only — no anonymous persistence
  const [bubble, setBubble] = useState(null); // { word, rect, turn, tokenIdx }
  const [selected, setSelected] = useState(null); // { word, occurrence }
  const [structureWord, setStructureWord] = useState(null); // morphology payload for the blow-up

  // Synced-video highlight (Listening step of YouTube topics)
  const [activeTurnId, setActiveTurnId] = useState(null);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const syncPlayerRef = useRef(null);
  const pausedAtEndRef = useRef(false);
  // Last line auto-scrolled to, so the transcript scrolls once per line change.
  const lastSyncedTurnRef = useRef(null);
  // The scenario's scroll container (set by PublicScenario via sectionRef).
  const scenarioSectionRef = useRef(null);

  // Desktop = the lg breakpoint where the word-detail sidebar shows. There the
  // video renders inside the sidebar (like the app's Word Detail slot); below
  // it the video is inline above/beside the transcript.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  const tts = usePublicTTS(token);
  const hasToken = !!localStorage.getItem("token");

  useEffect(() => {
    let cancelled = false;
    fetchPublicTopic(token)
      .then((t) => { if (!cancelled) { setTopic(t); track("share-topic-view"); } })
      .catch(() => { if (!cancelled) setGone(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Title + noindex: tokenized, revocable pages must stay out of search indexes.
  useEffect(() => {
    document.title = topic ? `${topic.name} — مراجعة` : "مراجعة";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, [topic]);

  const words = useMemo(() => topic?.words || [], [topic]);
  const turns = useMemo(() => topic?.scenario_turns || [], [topic]);
  const isYouTubeTopic = !!topic?.youtube_video_id;

  const timedTurns = useMemo(
    () =>
      turns
        .filter((t) => t.video_start_ms != null)
        .sort((a, b) => a.video_start_ms - b.video_start_ms),
    [turns]
  );

  // Player time → active line/word (same fuzz logic as TopicDetail); pauses at
  // the end of the transcribed segment and counts one local listening play.
  const handleVideoTime = useCallback(
    (ms) => {
      if (!timedTurns.length) return;
      const FUZZ = 120;
      let active = null;
      for (const t of timedTurns) {
        if (t.video_start_ms <= ms + FUZZ) active = t;
        else break;
      }
      if (!active) {
        setActiveTurnId(null);
        setActiveWordIndex(-1);
        return;
      }
      let wi = -1;
      const wt = active.word_timings;
      if (Array.isArray(wt)) {
        for (let i = 0; i < wt.length; i++) {
          if (wt[i].start_ms <= ms + FUZZ) wi = i;
          else break;
        }
      }
      setActiveTurnId((prev) => (prev === active.id ? prev : active.id));
      setActiveWordIndex((prev) => (prev === wi ? prev : wi));

      // Follow playback: scroll the transcript to the active line, within its
      // own box so the page (and the sidebar video) stays put — same behaviour
      // as the app. scrollIntoView is the fallback when the box isn't
      // scrollable.
      if (lastSyncedTurnRef.current !== active.id) {
        lastSyncedTurnRef.current = active.id;
        const el = tts.turnRefsRef.current[active.id];
        const container = scenarioSectionRef.current;
        if (el && container && container.scrollHeight > container.clientHeight + 4) {
          const elRect = el.getBoundingClientRect();
          const cRect = container.getBoundingClientRect();
          const delta =
            elRect.top - cRect.top - (container.clientHeight - el.clientHeight) / 2;
          container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
        } else {
          el?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
        }
      }

      const endMs = timedTurns.reduce((m, t) => Math.max(m, t.video_end_ms), 0);
      if (endMs && ms >= endMs) {
        if (!pausedAtEndRef.current) {
          pausedAtEndRef.current = true;
          syncPlayerRef.current?.pause();
          setPlayCount((c) => c + 1);
        }
      } else {
        pausedAtEndRef.current = false;
      }
    },
    [timedTurns, tts.turnRefsRef]
  );

  const handleTokenClick = (word, rect, turn, tokenIdx) => {
    setBubble({ word, rect, turn, tokenIdx });
  };

  const selectWord = (word, occurrence = null) => {
    setSelected({ word, occurrence });
  };

  const clearWord = () => setSelected(null);

  const handleSelectStep = (key) => {
    setViewStep(key);
    tts.stop();
    if (key === "roleplay" || key === "quiz") track("share-topic-locked-step", { step: key });
  };

  const progression = publicSteps(viewStep, Math.min(playCount, LISTENING_PLAYS_REQUIRED), !isYouTubeTopic);

  const showVideo = isYouTubeTopic && viewStep === "listening";

  // Per-line video play/pause (the red button in the transcript, like the app).
  const videoControls = showVideo
    ? {
        isVideoPlaying,
        activeTurnId,
        onToggle: (turn) => {
          const p = syncPlayerRef.current;
          if (!p) return;
          const isActiveLine = activeTurnId === turn.id;
          if (isVideoPlaying && isActiveLine) p.pause();
          else if (isActiveLine) p.play(); // resume from here
          else p.seekToMs(turn.video_start_ms); // jump + play
        },
      }
    : null;

  const videoPlayer = showVideo && (
    <>
      <YouTubeSyncPlayer
        ref={syncPlayerRef}
        videoId={topic?.youtube_video_id}
        startSeconds={topic?.youtube_start_seconds || 0}
        onTime={handleVideoTime}
        onStateChange={(s) => setIsVideoPlaying(s === 1)}
      />
      {timedTurns.length === 0 && (
        <p className="text-xs mt-2" style={{ color: "var(--parchment-dim)" }}>
          No synced timing for this video — the transcript won't auto-highlight.
        </p>
      )}
    </>
  );

  const scenario = (opts = {}) => (
    <PublicScenario
      turns={turns}
      words={words}
      tts={tts}
      onTokenClick={handleTokenClick}
      selectedWordId={bubble?.word?.id ?? selected?.word?.id ?? null}
      showTurnPlay={!isYouTubeTopic}
      {...opts}
    />
  );

  const signupCard = (
    <div className="surface-glass rounded-2xl px-4 py-5 text-center">
      <p className="font-amiri text-2xl mb-1" style={{ color: "var(--parchment)" }} dir="rtl">
        مراجعة
      </p>
      <p className="text-sm mb-4" style={{ color: "var(--parchment-dim)" }}>
        Tap any Arabic word to see its meaning here. Like this topic? Learn it
        properly — with quizzes, role play, and spaced review.
      </p>
      <a
        href="/?signup=1"
        onClick={() => track("share-topic-signup-click", { step: "sidebar" })}
        className="inline-block px-4 py-2 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter] no-underline"
      >
        Start your 7-day free trial
      </a>
    </div>
  );

  let body = null;
  if (loading) {
    body = (
      <div className="flex justify-center py-16" role="status" aria-label="Loading topic">
        <span className="w-14 h-14 rounded-full animate-spin border-4" style={{ borderColor: "var(--gold-dim)", borderTopColor: "var(--gold)" }} />
      </div>
    );
  } else if (gone || !topic) {
    body = (
      <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-lg mx-auto px-6 py-10 text-center">
        <p className="font-amiri text-3xl mb-2" style={{ color: "var(--parchment)" }}>هذا الرابط لم يعد متاحًا</p>
        <p className="text-sm mb-6" style={{ color: "var(--parchment-dim)" }}>
          This link is no longer available. The topic may have been unshared by its author.
        </p>
        <a
          href="/?signup=1"
          onClick={() => track("share-topic-signup-click", { step: "gone" })}
          className="inline-block px-5 py-2.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter] no-underline"
        >
          Start your 7-day free trial
        </a>
      </div>
    );
  } else {
    const stepContent = (
      <>
        {viewStep === "reading" && scenario()}

        {viewStep === "vocabulary" && (
          <PublicVocabList
            words={words}
            onSelectWord={(w) => selectWord(w)}
            onPlayWord={tts.playWord}
            playingWordId={tts.playingWordId}
          />
        )}

        {viewStep === "listening" && !isYouTubeTopic && (
          <div className="flex flex-col gap-3">
            <ListeningPanel
              playCount={Math.min(playCount, LISTENING_PLAYS_REQUIRED)}
              required={LISTENING_PLAYS_REQUIRED}
              isPlaying={!!(tts.playingTurnId || tts.loadingTurnId)}
              locked={false}
              done={playCount >= LISTENING_PLAYS_REQUIRED}
              onPlay={() =>
                tts.playAll(turns, {
                  onComplete: () => setPlayCount((c) => c + 1),
                  autoScroll: false,
                })
              }
              onStop={tts.stop}
            />
            {scenario({ maxHeightClass: "max-h-[28rem]" })}
          </div>
        )}

        {viewStep === "listening" && isYouTubeTopic && (
          // Desktop: the video lives in the word-detail sidebar (like the app);
          // the transcript gets the full main column. Below lg it renders
          // inline — sticky on top on mobile, beside the transcript on tablets.
          <div className={isDesktop ? "flex flex-col gap-3" : "flex flex-col md:flex-row md:items-start gap-3"}>
            {!isDesktop && (
              <div className="sticky top-0 z-20 pb-2 mb-1 md:static md:pb-0 md:mb-0 md:w-[44%] md:flex-none md:self-start" style={{ background: "var(--ink)" }}>
                {videoPlayer}
              </div>
            )}
            <div className="md:flex-1 md:min-w-0">
              {scenario({
                maxHeightClass: isDesktop ? "max-h-[32rem]" : "max-h-[50vh] md:max-h-[32rem]",
                activeTurnId,
                activeWordIndex,
                videoControls,
                sectionRef: scenarioSectionRef,
              })}
            </div>
          </div>
        )}

        {(viewStep === "roleplay" || viewStep === "quiz") && <PublicLockedStep stepKey={viewStep} />}
      </>
    );

    body = (
      <div className="w-full max-w-6xl mx-auto">
        {/* Topic header — above the grid so the sidebar card top-aligns with
            the Guided Journey card, not with the title. */}
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-cormorant font-semibold text-3xl leading-tight" style={{ color: "var(--parchment)" }}>
              {topic.name}
            </h1>
            {topic.description && (
              <p className="text-sm mt-1" style={{ color: "var(--parchment-dim)" }}>{topic.description}</p>
            )}
          </div>
          {/* Re-share: the admin's link is already public, so any visitor
              can pass it on — no account, no API call (see PublicShareButton). */}
          <PublicShareButton token={token} step={viewStep} />
        </div>

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-6 lg:items-start">
          <div className="min-w-0">
            <GuidedTopicJourney progression={progression} viewStep={viewStep} onSelectStep={handleSelectStep}>
              {stepContent}
            </GuidedTopicJourney>
          </div>

          {/* Desktop sidebar: synced video (Listening step) above the word
              detail / signup card — mirrors the app's Word Detail slot. */}
          <aside className="hidden lg:block sticky top-4">
            {isDesktop && showVideo && (
              <div className="mb-4 pb-4" style={{ borderBottom: "1px solid var(--panel-line)" }}>
                {videoPlayer}
              </div>
            )}
            {selected ? (
              <PublicWordDetail
                word={selected.word}
                occurrence={selected.occurrence}
                onClear={clearWord}
                onPlay={tts.playWord}
                isPlaying={tts.playingWordId === selected.word.id}
              />
            ) : (
              signupCard
            )}
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="dark-scope min-h-screen flex flex-col" style={{ background: "var(--ink)" }}>
      {/* Header — same shell as PublicIrabPage */}
      <header className="flex items-center justify-between px-5 py-4 max-w-6xl w-full mx-auto">
        <a href="https://muraja.app" className="flex items-baseline gap-2 no-underline">
          <span className="font-amiri text-2xl text-dt-gold-bright">مراجعة</span>
          <span className="text-xs text-dt-parchment-dim tracking-wide">murājaʿa</span>
        </a>
        <a
          href="/"
          onClick={() => track("share-topic-signup-click", { step: "header" })}
          className="text-sm text-dt-parchment-dim hover:text-dt-gold-bright transition-colors"
        >
          {hasToken ? "Open the app →" : "Sign in →"}
        </a>
      </header>

      <main className="flex-1 px-3 sm:px-5 pb-10">{body}</main>

      {/* Word bubble */}
      {bubble && (() => {
        // Structure for the tapped token: the occurrence-specific entry wins,
        // falling back to word-level morphology — the same precedence
        // PublicWordDetail uses, so ◇ shows whenever the panel's gold
        // "View Word Structure" button would.
        const occMorph = (bubble.turn?.word_morphology || {})[String(bubble.tokenIdx)] || null;
        const bubbleMorph = occMorph || bubble.word?.morphology || null;
        return (
        <PublicWordBubble
          word={bubble.word}
          anchorRect={bubble.rect}
          onClose={() => setBubble(null)}
          onPlay={tts.playWord}
          isPlaying={tts.playingWordId === bubble.word.id}
          hasStructure={!!bubbleMorph}
          onStructureClick={() => {
            track("share-topic-structure-view", { source: "bubble" });
            setStructureWord(bubbleMorph);
          }}
          onInfoClick={() => {
            const surface = (bubble.turn?.arabic || "").split(/\s+/)[bubble.tokenIdx];
            // Pass only the occurrence entry — PublicWordDetail applies its
            // own word-level fallback, keeping the original semantics.
            selectWord(bubble.word, { surfaceText: surface, morphology: occMorph });
          }}
        />
        );
      })()}

      {/* Word-structure blow-up straight from the bubble's ◇ (the info panel
          keeps its own instance inside PublicWordDetail). */}
      {structureWord && (
        <WordMorphology word={structureWord} onClose={() => setStructureWord(null)} />
      )}

      {/* Mobile word-detail bottom sheet */}
      {selected && (
        <div className="lg:hidden fixed inset-0 z-30 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={clearWord} aria-hidden="true" />
          <div
            className="relative w-full max-h-[88vh] flex flex-col rounded-t-2xl shadow-2xl animate-rise"
            style={{ background: "var(--ink)", borderTop: "1px solid var(--panel-line)" }}
          >
            <div className="shrink-0 px-4 pt-2.5 pb-2" style={{ borderBottom: "1px solid var(--panel-line)" }}>
              <div className="w-9 h-1 rounded-full mx-auto mb-2" style={{ background: "var(--panel-line)" }} />
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide" style={{ color: "var(--parchment-dim)" }}>Word detail</span>
                <button
                  onClick={clearWord}
                  className="text-xl leading-none"
                  style={{ color: "var(--parchment-dim)" }}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 scroll-elegant">
              <PublicWordDetail
                word={selected.word}
                occurrence={selected.occurrence}
                onClear={null}
                onPlay={tts.playWord}
                isPlaying={tts.playingWordId === selected.word.id}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer — same shell as PublicIrabPage */}
      <footer className="px-5 py-4 text-center text-xs text-dt-parchment-dim space-x-3">
        <a href="https://muraja.app" className="hover:text-dt-parchment">muraja.app</a>
        <a href="https://muraja.app/privacy.html" className="hover:text-dt-parchment">Privacy</a>
        <a href="https://muraja.app/terms.html" className="hover:text-dt-parchment">Terms</a>
        <a href="https://muraja.app/ai-policy.html" className="hover:text-dt-parchment">AI Policy</a>
      </footer>
    </div>
  );
}
